/* ============================================================
   Create or edit a habit: name, icon, colour, reps per day, and the cadence
   (the yardstick, not a projection — docs/habit-vs-recurring-task.md). A new
   habit goes through `saveCapture` with `habit: true`, exactly as the mobile
   capture sheet does, so it appends and starts today.
   ============================================================ */
import React, { useState } from 'react';
import { DAILY_SCHEDULE, type HabitSchedule, MAX_TIMES_PER_WEEK, WEEKDAY_CODES, WEEKDAY_LABELS } from '@habishine/shared';
import { type Habit, type HueName, useStore } from '@habishine/client-core';

import { track } from '../analytics';
import { Dialog, inputClass, Label } from './Dialog';
import { HuePicker } from './HuePicker';
import { Button } from './ui';

// the mobile capture sheet's set
const EMOJI = ['✳️', '💧', '📖', '🧘', '🏃', '☕️', '🌱', '🎯', '💪', '🍎', '📝', '🎨'];

type Kind = HabitSchedule['kind'];

export function HabitEditor({ habit, onClose }: { habit: Habit | null; onClose: () => void }) {
  const { saveCapture, updateHabit, deleteHabit } = useStore();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [name, setName] = useState(habit?.name ?? '');
  const [emoji, setEmoji] = useState(habit?.emoji ?? '');
  const [hue, setHue] = useState<HueName>(habit?.hue ?? 'green');
  const [target, setTarget] = useState(habit?.target ?? 1);
  const [kind, setKind] = useState<Kind>(habit?.schedule.kind ?? 'daily');
  const [days, setDays] = useState<string[]>(habit?.schedule.kind === 'weekdays' ? habit.schedule.days : ['MO', 'TU', 'WE', 'TH', 'FR']);
  const [times, setTimes] = useState(habit?.schedule.kind === 'timesPerWeek' ? habit.schedule.times : 3);

  const schedule = (): HabitSchedule => {
    if (kind === 'weekdays') return { kind, days: WEEKDAY_CODES.filter((c) => days.includes(c)) };
    if (kind === 'timesPerWeek') return { kind, times: Math.min(MAX_TIMES_PER_WEEK, Math.max(1, times)) };
    return DAILY_SCHEDULE;
  };

  const save = (e: React.FormEvent) => {
    e.preventDefault();
    const n = name.trim();
    if (!n) return;
    if (habit) {
      updateHabit(habit.id, { name: n, emoji, hue, target, schedule: schedule() });
    } else {
      saveCapture(n, {
        emoji, hue, today: true, habit: true, target, schedule: schedule(),
        recurrence: null, tagIds: [], projectId: null, remindAt: null,
      });
      track('habit_created', { scheduled: true });
    }
    onClose();
  };

  return (
    <Dialog
      title={habit ? 'Edit habit' : 'New habit'}
      onClose={onClose}
      footer={
        <>
          {habit ? (
            confirmDelete ? (
              <Button type="button" variant="danger" className="mr-auto" onClick={() => { deleteHabit(habit.id); onClose(); }}>Delete for good</Button>
            ) : (
              <Button type="button" variant="ghost" className="mr-auto text-tertiary" onClick={() => setConfirmDelete(true)}>Delete…</Button>
            )
          ) : null}
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" form="habit-editor" variant="primary" disabled={!name.trim()}>{habit ? 'Save' : 'Create'}</Button>
        </>
      }
    >
      <form id="habit-editor" onSubmit={save} className="space-y-4">
        <label className="block">
          <Label>NAME</Label>
          <input className={inputClass} value={name} onChange={(e) => setName(e.target.value)} placeholder="Read ten pages" autoFocus />
        </label>
        <div>
          <Label>ICON</Label>
          <div className="flex flex-wrap gap-1">
            {EMOJI.map((e) => (
              <button key={e} type="button" aria-pressed={emoji === e} onClick={() => setEmoji(emoji === e ? '' : e)}
                className={`h-10 w-10 rounded-[12px] text-xl transition-transform duration-150 hover:scale-110 ${emoji === e ? 'bg-frog-soft shadow-[0_0_0_2px_var(--frog)]' : 'hover:bg-card-sunk'}`}>{e}</button>
            ))}
          </div>
        </div>
        <div>
          <Label>COLOUR</Label>
          <HuePicker value={hue} onChange={setHue} />
        </div>
        <label className="block">
          <Label>TIMES PER DAY</Label>
          <input type="number" min={1} max={20} className={`${inputClass} w-24`} value={target} onChange={(e) => setTarget(Math.max(1, Number(e.target.value) || 1))} />
        </label>
        <div>
          <Label>WHICH DAYS</Label>
          <div className="flex gap-2">
            {([['daily', 'Every day'], ['weekdays', 'Some days'], ['timesPerWeek', 'A few times a week']] as [Kind, string][]).map(([k, label]) => (
              <Button key={k} type="button" variant={kind === k ? 'primary' : 'secondary'} onClick={() => setKind(k)}>{label}</Button>
            ))}
          </div>
          {kind === 'weekdays' ? (
            <div className="mt-2 flex gap-1">
              {WEEKDAY_CODES.map((c, i) => (
                <button key={c} type="button" aria-pressed={days.includes(c)}
                  onClick={() => setDays((d) => (d.includes(c) ? d.filter((x) => x !== c) : [...d, c]))}
                  className={`rounded-full px-2.5 py-1 text-xs font-semibold ${days.includes(c) ? 'frog-gradient text-white' : 'bg-card-sunk text-ink-2'}`}>{WEEKDAY_LABELS[i]}</button>
              ))}
            </div>
          ) : null}
          {kind === 'timesPerWeek' ? (
            <label className="mt-2 block text-sm text-ink-2">
              <input type="number" min={1} max={MAX_TIMES_PER_WEEK} className={`${inputClass} mr-2 inline-block w-20`} value={times} onChange={(e) => setTimes(Number(e.target.value) || 1)} /> days a week
            </label>
          ) : null}
        </div>
      </form>
    </Dialog>
  );
}
